/**
 * Retry policy for the callables this app makes.
 *
 * Customers order on venue wifi, which is often slow and drops requests
 * halfway. Two rules follow from that, and they are deliberately not the same
 * rule:
 *
 * - A READ can be asked again. The worst a repeat costs is another round trip,
 *   so a dropped request becomes a pause instead of a dead end.
 * - A WRITE must NOT be retried automatically. `location-order-send` mints the
 *   order id on the server, so a request that reached the venue but whose reply
 *   was lost looks exactly like one that never arrived. Retrying it would place
 *   the order twice. Writes stay single shot until they carry a client-supplied
 *   id the server can de-duplicate on.
 */

/**
 * Callable error codes that mean the request never got a verdict, so asking
 * again is meaningful. Two exclusions are deliberate: `internal`, which our
 * functions throw for real rejections (an order the POS refused), and
 * `resource-exhausted`, which is a quota answer — re-asking only adds load to
 * something already out of capacity.
 */
const RETRYABLE_CODES = [
  "unavailable",
  "deadline-exceeded",
  "aborted",
  "cancelled",
];

/** Backoff between attempts. One extra entry per extra attempt. */
const RETRY_DELAYS_MS = [500, 1500];

/** Well short of the callable default of 70s, which is a lifetime on a phone. */
export const READ_TIMEOUT_MS = 10_000;

/** Shown when a request fell over on the connection rather than on its answer. */
export const CONNECTION_ERROR = "Check your connection and try again.";

export const isOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * A request the network killed usually carries no callable code at all, so no
 * code counts as retryable.
 */
export const isRetryable = (err: unknown): boolean => {
  const code = (err as { code?: string } | null)?.code;
  if (!code) return true;
  return RETRYABLE_CODES.some((retryable) => code.includes(retryable));
};

/**
 * The message to show a customer for a failed request: the connection line when
 * the request never got an answer, otherwise whatever the caller knows to say.
 * Keeps the offline/retryable decision in one place rather than at each catch.
 */
export const connectionErrorOr = (err: unknown, fallback: string) =>
  isOffline() || isRetryable(err) ? CONNECTION_ERROR : fallback;

export const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Runs `call`, repeating it on a transient failure with a growing pause in
 * between. Only for reads — see the note at the top of this file.
 */
export const withRetry = async <T>(call: () => Promise<T>): Promise<T> => {
  const attempts = RETRY_DELAYS_MS.length + 1;

  for (let attempt = 0; ; attempt++) {
    try {
      return await call();
    } catch (err) {
      if (attempt >= attempts - 1 || !isRetryable(err)) throw err;
      await wait(RETRY_DELAYS_MS[attempt]);
    }
  }
};
